import type { WorkflowBranch, WorkflowNode, WorkflowSummary } from '../../../types/backend';
import { toStages } from '../../workflow/utils/workflowStages';
import { grantProblem } from '../../workflow/utils/toolGrants';

// The editor's model of a workflow: an ordered list of stages, each a list of steps. A stage's
// layer is its position, so stages can be added, removed and moved without a layer number ever
// going stale; only branch targets carry a layer, and every operation that moves a stage remaps
// them. Each operation either returns the new stages or refuses with a sentence for the
// operator, so a plan the backend would reject for its shape is never built in the first place.
// The backend stays the authority on everything else and its messages are shown as they come.

export const DRAFT_VERSION = 'DRAFT';

/**
 * A step while it is being edited. `key` is the editor's own handle on it, not the backend's: a
 * step's `id` follows its name as the operator types, and its position moves whenever a stage is
 * added, moved or split, so neither can tell the editor which step a form is open on. The key is
 * assigned once and never changes, and never leaves the Studio.
 */
export type DraftStep = WorkflowNode & { key: string };

export type DraftStages = DraftStep[][];

let keySeq = 0;
export const draftKey = (): string => `draft-step-${(keySeq += 1)}`;

export type DraftResult = { stages: DraftStages } | { refused: string };

/** The id pattern the backend holds node ids and workflow names to (shared_kernel NAME_PATTERN). */
export const NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
const MAX_ID_LENGTH = 60;

/**
 * A snake_case key from free text: what an id or a name is derived from until someone edits it.
 * `prefix` is what a slug starting with a digit is given, since the pattern wants a letter first.
 */
export const toSnakeCase = (text: string, max = MAX_ID_LENGTH, prefix = 'step'): string => {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) return '';
  return (/^[a-z]/.test(slug) ? slug : `${prefix}_${slug}`).slice(0, max).replace(/_+$/, '');
};

export const emptyStep = (layer: number): DraftStep => ({
  key: draftKey(),
  id: '',
  label: '',
  layer,
  kind: 'step',
  goal: '',
  gate: false,
  depends_on: [],
  branches: [],
  allowed_tools: [],
  evidence_required: [],
  inputs: [],
});

/** Every optional field spelled out, so a stored node and an edited one compare equal. */
const complete = (node: WorkflowNode, layer: number): WorkflowNode => ({
  id: node.id,
  label: node.label,
  layer,
  kind: node.kind ?? 'step',
  goal: node.goal,
  gate: node.gate ?? false,
  depends_on: [...(node.depends_on ?? [])],
  branches: (node.branches ?? []).map((b) => ({ ...b })),
  allowed_tools: [...(node.allowed_tools ?? [])],
  evidence_required: [...(node.evidence_required ?? [])],
  inputs: [...(node.inputs ?? [])],
});

/** Loading a stored workflow gives each step its editing key. */
export const stagesOf = (nodes: WorkflowNode[]): DraftStages =>
  toStages(nodes).map((stage, i) =>
    stage.nodes.map((n) => ({ ...complete(n, i + 1), key: draftKey() })),
  );

/** The plan as the backend takes it. The editing key never travels. */
export const nodesOf = (stages: DraftStages): WorkflowNode[] =>
  stages.flatMap((members, i) => members.map((n) => complete(n, i + 1)));

const ok = (stages: DraftStages): DraftResult => ({ stages });
const refuse = (refused: string): DraftResult => ({ refused });

const labelOf = (node: WorkflowNode) => node.label || node.id || 'an untitled step';

/**
 * Re-lay the stages after a structural change. `newLayerOf` maps an old layer to its new one, and
 * every branch target goes through it, so a route keeps pointing at the same stage it did.
 */
const relayer = (stages: DraftStages, newLayerOf: (oldLayer: number) => number): DraftStages =>
  stages.map((members, i) =>
    members.map((node) => ({
      ...node,
      layer: i + 1,
      branches: (node.branches ?? []).map((b) => ({ ...b, target_layer: newLayerOf(b.target_layer) })),
    })),
  );

/** A route that would no longer run forward, named for the operator; null when every route does. */
const backwardRoute = (stages: DraftStages): string | null => {
  for (const [i, members] of stages.entries()) {
    for (const node of members) {
      const back = (node.branches ?? []).find((b) => b.target_layer <= i + 1);
      if (back) {
        return (
          `route '${back.condition || 'untitled'}' on '${labelOf(node)}' would go to Stage ` +
          `${back.target_layer}, at or before its own Stage ${i + 1}. Workflows only run forward.`
        );
      }
    }
  }
  return null;
};

export const addStage = (stages: DraftStages): DraftResult => ok([...stages, []]);

export const moveStage = (stages: DraftStages, from: number, to: number): DraftResult => {
  if (to < 0 || to >= stages.length || from === to) return ok(stages);
  const order = stages.map((_, i) => i);
  order.splice(to, 0, ...order.splice(from, 1));
  const moved = order.map((old) => stages[old]);
  const newLayerOf = (oldLayer: number) => order.indexOf(oldLayer - 1) + 1;
  const next = relayer(moved, newLayerOf);
  const problem = backwardRoute(next);
  return problem ? refuse(`Stage ${from + 1} cannot move there: ${problem}`) : ok(next);
};

export const removeStage = (stages: DraftStages, index: number): DraftResult => {
  const layer = index + 1;
  for (const [i, members] of stages.entries()) {
    if (i === index) continue;
    for (const node of members) {
      const into = (node.branches ?? []).find((b) => b.target_layer === layer);
      if (into) {
        return refuse(
          `Stage ${layer} is where route '${into.condition || 'untitled'}' on ` +
            `'${labelOf(node)}' goes. Point that route at another stage first.`,
        );
      }
    }
  }
  const kept = stages.filter((_, i) => i !== index);
  return ok(relayer(kept, (old) => (old > layer ? old - 1 : old)));
};

const replaceStage = (stages: DraftStages, index: number, members: DraftStep[]): DraftStages =>
  stages.map((m, i) => (i === index ? members : m));

const hasDecision = (members: WorkflowNode[]) => members.some((n) => n.kind === 'decision');

export const addStep = (stages: DraftStages, stageIndex: number): DraftResult => {
  const members = stages[stageIndex];
  if (hasDecision(members)) {
    return refuse(
      `Stage ${stageIndex + 1} is a decision, and a decision is a stage on its own. ` +
        'Add the step to another stage.',
    );
  }
  return ok(replaceStage(stages, stageIndex, [...members, emptyStep(stageIndex + 1)]));
};

export const removeStep = (stages: DraftStages, stageIndex: number, stepIndex: number): DraftResult => {
  const members = stages[stageIndex];
  const gone = members[stepIndex].id;
  const rest = members
    .filter((_, i) => i !== stepIndex)
    .map((n) => ({ ...n, depends_on: (n.depends_on ?? []).filter((d) => d !== gone) }));
  return ok(replaceStage(stages, stageIndex, rest));
};

export const updateStep = (
  stages: DraftStages,
  stageIndex: number,
  stepIndex: number,
  patch: Partial<WorkflowNode>,
): DraftResult => {
  const members = stages[stageIndex];
  const node = members[stepIndex];
  const next: DraftStep = { ...node, ...patch, key: node.key, layer: stageIndex + 1 };

  // The id follows the label until someone types an id of their own.
  if (patch.label !== undefined && patch.id === undefined) {
    const derivedBefore = node.id === '' || node.id === toSnakeCase(node.label);
    if (derivedBefore) next.id = toSnakeCase(patch.label);
  }

  // A kind change that would discard what the operator wrote is refused, never applied silently.
  // Where nothing would be lost, it is carried out instead of being handed back as homework.
  if (patch.kind === 'decision' && node.kind !== 'decision') {
    if ((node.depends_on ?? []).length > 0) {
      return refuse(
        `'${labelOf(node)}' waits on other steps in this stage, and a decision waits on none. ` +
          'Clear the "Runs after" list first.',
      );
    }
    if ((node.evidence_required ?? []).length > 0) {
      return refuse(
        `'${labelOf(node)}' records results, and a decision records none: it records the route ` +
          'it took and why. Move those results to the step that finds them first.',
      );
    }
    if (members.length > 1) {
      const waiters = members.filter((n) => (n.depends_on ?? []).includes(node.id));
      if (waiters.length > 0) {
        return refuse(
          `${waiters.map(labelOf).map((l) => `'${l}'`).join(' and ')} wait on ` +
            `'${labelOf(node)}', and a decision goes into a stage of its own, which would put it ` +
            'after them. Clear that from their "Runs after" lists first.',
        );
      }
      // A decision owns its stage, so making one here splits the stage rather than refusing:
      // the decision moves into a new stage directly after, and every route after it shifts.
      const kept = members.filter((_, i) => i !== stepIndex);
      const split: DraftStages = [
        ...stages.slice(0, stageIndex),
        kept,
        [next],
        ...stages.slice(stageIndex + 1),
      ];
      return ok(relayer(split, (old) => (old > stageIndex + 1 ? old + 1 : old)));
    }
  }
  if (patch.kind === 'step' && (node.branches ?? []).length > 0) {
    return refuse(`'${labelOf(node)}' has routes, and a step has none. Remove its routes first.`);
  }

  const renamed = next.id !== node.id;
  const updated = members.map((n, i) => {
    if (i === stepIndex) return next;
    if (!renamed) return n;
    return { ...n, depends_on: (n.depends_on ?? []).map((d) => (d === node.id ? next.id : d)) };
  });
  return ok(replaceStage(stages, stageIndex, updated));
};

const updateBranches = (
  stages: DraftStages,
  stageIndex: number,
  stepIndex: number,
  change: (branches: WorkflowBranch[]) => WorkflowBranch[],
): DraftResult => {
  const node = stages[stageIndex][stepIndex];
  return updateStep(stages, stageIndex, stepIndex, { branches: change(node.branches ?? []) });
};

export const addRoute = (stages: DraftStages, stageIndex: number, stepIndex: number): DraftResult => {
  if (stageIndex + 1 >= stages.length) {
    return refuse('A route only goes forward, so add a later stage before adding a route.');
  }
  return updateBranches(stages, stageIndex, stepIndex, (branches) => [
    ...branches,
    { condition: '', target_layer: stageIndex + 2 },
  ]);
};

export const updateRoute = (
  stages: DraftStages,
  stageIndex: number,
  stepIndex: number,
  routeIndex: number,
  patch: Partial<WorkflowBranch>,
): DraftResult => {
  if (patch.target_layer !== undefined && patch.target_layer <= stageIndex + 1) {
    return refuse('A route only goes forward: pick a stage after this one.');
  }
  return updateBranches(stages, stageIndex, stepIndex, (branches) =>
    branches.map((b, i) => (i === routeIndex ? { ...b, ...patch } : b)),
  );
};

export const removeRoute = (
  stages: DraftStages,
  stageIndex: number,
  stepIndex: number,
  routeIndex: number,
): DraftResult =>
  updateBranches(stages, stageIndex, stepIndex, (branches) =>
    branches.filter((_, i) => i !== routeIndex),
  );

/**
 * Stages a run can never enter, mirroring WorkflowPlan.reachable_layers: Stage 1 is always
 * entered, a stage holding a decision leads only to its route targets, and any other stage falls
 * through to the next.
 */
export const unreachableLayers = (stages: DraftStages): number[] => {
  const reachable = new Set([1]);
  stages.forEach((members, i) => {
    const layer = i + 1;
    if (!reachable.has(layer)) return;
    if (hasDecision(members)) {
      members.forEach((n) => (n.branches ?? []).forEach((b) => reachable.add(b.target_layer)));
    } else if (layer < stages.length) {
      reachable.add(layer + 1);
    }
  });
  return stages.map((_, i) => i + 1).filter((layer) => !reachable.has(layer));
};

export const unreachableMessage = (layer: number) =>
  `Stage ${layer} cannot be reached: no route leads to it.`;

/**
 * Fact keys as the backend accepts them: snake_case, none blank, each once. Free text is turned
 * into a key rather than refused, so the operator sees what will be saved while they type.
 */
export const evidenceKeys = (values: string[]): string[] => [
  ...new Set(values.map((v) => toSnakeCase(v, MAX_ID_LENGTH, 'fact')).filter(Boolean)),
];

/** Tool names as the backend accepts them: trimmed, none blank, each once. */
export const toolNames = (values: string[]): string[] => [
  ...new Set(values.map((v) => v.trim()).filter(Boolean)),
];

/** The stages a run can be in after this one. */
const nextStages = (stages: DraftStages, index: number): number[] => {
  const decision = (stages[index] ?? []).find((n) => n.kind === 'decision');
  if (decision) return (decision.branches ?? []).map((b) => b.target_layer);
  return index + 1 < stages.length ? [index + 2] : [];
};

/**
 * The results every route into this stage is guaranteed to have written down already.
 *
 * The intersection over routes, not the union: a result written on one branch of a decision is
 * absent on the others, and a step that reads it would arrive with nothing on those routes. The
 * backend refuses such a plan, so the editor offers only what it would accept.
 */
export const resultsAvailableAt = (stages: DraftStages, stageIndex: number): string[] => {
  const guaranteed = new Map<number, Set<string>>([[1, new Set()]]);
  stages.forEach((members, i) => {
    const here = guaranteed.get(i + 1);
    if (!here) return;
    const carried = new Set(here);
    members.forEach((n) => (n.evidence_required ?? []).forEach((k) => carried.add(k)));
    nextStages(stages, i).forEach((layer) => {
      const known = guaranteed.get(layer);
      if (!known) guaranteed.set(layer, new Set(carried));
      else [...known].forEach((k) => !carried.has(k) && known.delete(k));
    });
  });
  return [...(guaranteed.get(stageIndex + 1) ?? [])].sort();
};

/** What a step may read: the guaranteed results, plus those of the steps it runs after. */
export const resultsAvailableTo = (
  stages: DraftStages,
  stageIndex: number,
  stepIndex: number,
): string[] => {
  const members = stages[stageIndex] ?? [];
  const node = members[stepIndex];
  const available = new Set(resultsAvailableAt(stages, stageIndex));
  if (node) {
    const byId = new Map(members.map((n) => [n.id, n]));
    const pending = [...(node.depends_on ?? [])];
    const seen = new Set<string>();
    while (pending.length > 0) {
      const id = pending.pop() as string;
      if (seen.has(id)) continue;
      seen.add(id);
      const dep = byId.get(id);
      if (!dep) continue;
      (dep.evidence_required ?? []).forEach((k) => available.add(k));
      pending.push(...(dep.depends_on ?? []));
    }
  }
  return [...available].sort();
};

export interface DraftFields {
  name: string;
  title: string;
  description: string;
  stages: DraftStages;
}

/**
 * The cheap rules, checked on save (FE-07): the ones whose breach makes a plan impossible to
 * build, a tool mode that does not exist, and an escalation tool. Reachability, cycles and
 * tool names are the backend's to judge, and its answer is shown.
 */
export const validateDraft = (draft: DraftFields, isNew: boolean): string[] => {
  const errors: string[] = [];
  if (isNew && !NAME_PATTERN.test(draft.name)) {
    errors.push('Name must be snake_case: lowercase letters, digits and underscores, starting with a letter.');
  }
  if (!draft.title.trim()) errors.push('Give the workflow a title.');
  if (draft.stages.length === 0) errors.push('Add at least one stage.');

  const seen = new Set<string>();
  draft.stages.forEach((members, i) => {
    const stage = `Stage ${i + 1}`;
    if (members.length === 0) errors.push(`${stage} has no steps.`);
    if (hasDecision(members) && members.length > 1) {
      errors.push(`${stage} holds a decision alongside other steps; a decision is alone in its stage.`);
    }
    members.forEach((node, ni) => {
      const available = new Set(resultsAvailableTo(draft.stages, i, ni));
      const stranded = (node.inputs ?? []).filter((key) => !available.has(key));
      if (stranded.length > 0) {
        errors.push(
          `${stage}: '${labelOf(node)}' reads ${stranded.join(', ')}, which no route reaching it ` +
            'is guaranteed to have written down.',
        );
      }
      const name = `${stage}, '${labelOf(node)}'`;
      if (!node.label.trim()) errors.push(`${stage} has a step with no label.`);
      if (!NAME_PATTERN.test(node.id)) errors.push(`${name}: the id must be snake_case.`);
      if (node.id && seen.has(node.id)) errors.push(`${name}: the id '${node.id}' is used twice.`);
      seen.add(node.id);
      if (!node.goal.trim()) errors.push(`${name}: say what the step must achieve.`);
      (node.allowed_tools ?? []).forEach((entry) => {
        const problem = grantProblem(entry);
        if (problem) errors.push(`${name}: ${problem}`);
      });
      const facts = node.evidence_required ?? [];
      facts.forEach((key, at) => {
        if (!NAME_PATTERN.test(key)) {
          errors.push(`${name}: the fact '${key}' is not a snake_case key.`);
        } else if (facts.indexOf(key) !== at) {
          errors.push(`${name}: the fact '${key}' is required twice.`);
        }
      });
      if (node.kind === 'decision') {
        if (facts.length > 0) {
          errors.push(
            `${name}: a decision records no facts, only the route it took and why; ` +
              'put them on the step that establishes them.',
          );
        }
        const routes = node.branches ?? [];
        if (routes.length < 2) errors.push(`${name}: a decision needs at least two routes.`);
        if (routes.some((r) => !r.condition.trim())) errors.push(`${name}: every route needs a condition.`);
        if (new Set(routes.map((r) => r.condition)).size !== routes.length) {
          errors.push(`${name}: two routes have the same condition.`);
        }
        if (routes.some((r) => r.target_layer <= i + 1 || r.target_layer > draft.stages.length)) {
          errors.push(`${name}: every route must go to a later stage.`);
        }
      }
    });
  });
  unreachableLayers(draft.stages).forEach((layer) => errors.push(unreachableMessage(layer)));
  return errors;
};

/**
 * Every version of one workflow, oldest first, with the draft last. The listing names only the
 * latest published version, and that is enough: publishing always takes the next integer and no
 * single version is ever deleted, so v1 to vN all exist.
 */
export const versionsOf = (summaries: WorkflowSummary[], workflowId: string): string[] => {
  const rows = summaries.filter((s) => s.workflow_id === workflowId);
  const latest = Math.max(0, ...rows.filter((s) => s.version !== DRAFT_VERSION).map((s) => Number(s.version)));
  const published = Array.from({ length: latest }, (_, i) => String(i + 1));
  return rows.some((s) => s.version === DRAFT_VERSION) ? [...published, DRAFT_VERSION] : published;
};

/**
 * One row per workflow for the Studio listing: its latest published version, or its draft where it
 * has never been published.
 *
 * Publishing keeps the draft row so authoring continues from where it left off, which means the
 * listing holds two rows for every workflow that has ever been published. Rendered flat, each one
 * appears twice under the same title and the footer counts it twice.
 */
export const listedWorkflows = (summaries: WorkflowSummary[]): WorkflowSummary[] => {
  const byWorkflow = new Map<string, WorkflowSummary>();
  summaries.forEach((row) => {
    const held = byWorkflow.get(row.workflow_id);
    if (!held || held.version === DRAFT_VERSION) byWorkflow.set(row.workflow_id, row);
  });
  return Array.from(byWorkflow.values());
};
